<template>
  <tr :class="rowClass">
    <td class="text-xs-left">
      <v-badge
        color="primary"
        left>
        <v-tooltip
          v-if="day.holiday"
          slot="badge"
          bottom>
          <v-icon
            slot="activator"
            dark
            small>weekend
          </v-icon>
          <span>{{ $t('holiday') }}</span>
        </v-tooltip>
        <span>{{ formattedDay }}</span>
      </v-badge>
    </td>
    <td class="text-xs-left">
      {{ workingTime }}
      <v-tooltip top>
        <template slot="activator">
          <v-icon v-if="unusualWorkTime">warning</v-icon>
        </template>
        <span>
          {{ $t('unusualWorkTimeWarning') }}
        </span>
      </v-tooltip>
    </td>
    <td class="text-xs-left">
      {{ day.is_leave_day ? '' : workedHours }}
    </td>
    <td class="text-xs-right">
      <template v-if="canWork">
        <employee-timesheet-editor-quick-action
          :tooltip="$t('changeWorkTime')"
          icon="edit"
          @click="editItem"
        />

        <employee-timesheet-editor-quick-action
          :tooltip="$t('deleteWorkTime')"
          icon="close"
          @click="removeHoursFromDay"
        />
        <dialog-timesheet-editor-day
          :value="dialog"
          :edited-item="editedItem"
          :break-time="schema.break_minutes"
          @dialogToggle="val => dialog = val"
          @save="save"
          @close="close"
        />
      </template>
    </td>
  </tr>
</template>

<script>
import EmployeeTimesheetEditorQuickAction
  from '@/components/routes/employees/timesheets/EmployeeTimesheetEditorQuickAction'
import DialogTimesheetEditorDay from '@/components/routes/employees/timesheets/DialogTimesheetEditorDay'

function getEmptyDay () {
  return {
    day: null,
    start_time: '08:00',
    end_time: '08:00'
  }
}

export default {
  name: 'EmployeeTimesheetEditorTableDay',
  components: { DialogTimesheetEditorDay, EmployeeTimesheetEditorQuickAction },
  props: [
    'day',
    'schema',
    'meanValueWorkTime'
  ],
  data () {
    return {
      dialog: false,
      editedItem: getEmptyDay(),
      initialWorkTime: {
        start_time: null,
        end_time: null
      }
    }
  },
  computed: {
    momentDay () {
      return this.$moment(this.day.day)
    },
    isAfterContractStart () {
      const started = this.schema.started_working_at_company
      if (!started) return true
      const $started = this.$moment(started)

      return this.momentDay.isAfter($started) || this.momentDay.isSame($started)
    },
    isBeforeContractEnd () {
      const stopped = this.schema.stopped_working_at_company
      if (!stopped) return true
      const $stopped = this.$moment(stopped)

      return this.momentDay.isBefore($stopped) || this.momentDay.isSame($stopped)
    },

    isHoliday () {
      return this.day.holiday && !this.schema.works_on_holidays
    },

    canWork () {
      return this.day.is_allowed_to_work
    },

    rowClass () {
      const classes = []

      if (this.canWork === false) {
        classes.push('disabled')
      }

      if (this.hasEditedWorktime) {
        classes.push('edited')
      }

      if (this.isSunday || this.isHoliday) {
        classes.push('holiday')
      }

      return classes
    },

    formattedDay () {
      return this.momentDay.format('Do MMMM YYYY, ddd')
    },
    workingTime () {
      if (!this.isAfterContractStart || !this.isBeforeContractEnd) {
        return this.$t('noContract')
      }
      if (this.day.is_leave_day) {
        return this.$t('isLeaveDay')
      }
      if (this.isHoliday) {
        return this.$t('isHoliday')
      }
      if (this.day.inactive) {
        return this.$t('isInactive')
      }

      if (this.day.start_time || this.day.end_time) {
        return `${this.day.start_time}-${this.day.end_time}`
      }
    },

    unusualWorkTime () {
      if (!this.day.worked_hours || this.schema.is_part_time_worker) return false

      const usualWorkTime = this.meanValueWorkTime
      const allowedDiff = 0.25
      const upper = 1 + allowedDiff
      const lower = 1 - allowedDiff
      const relativeValue = this.day.worked_hours / usualWorkTime
      return relativeValue < lower || relativeValue > upper
    },
    workedHours () {
      if (!this.day.worked_hours) return

      let [h, m] = this.day.worked_hours.toString().split('.')
      m = m ? Math.round(Number(`0.${m}`) * 60) : 0
      return this.$t('duration', { h, m })
    },
    hasEditedWorktime
    () {
      const s1 = this.initialWorkTime.start_time
      const s2 = this.day.start_time
      const e1 = this.initialWorkTime.end_time
      const e2 = this.day.end_time
      return this.day.changed_ex_post || s1 !== s2 || e1 !== e2
    },
    isSunday () {
      return Number(this.momentDay.format('E')) === 7
    }
  },
  methods: {
    removeHoursFromDay () {
      this.day.start_time = null
      this.day.end_time = null
      this.day.worked_hours = 0
      this.day.working = 0
      this.day.changed_ex_post = this.hasEditedWorktime
    },
    editItem () {
      const { day, start_time, end_time } = this.day
      const emptyDay = getEmptyDay()
      this.editedItem = {
        day,
        start_time: start_time || emptyDay.start_time,
        end_time: end_time || emptyDay.end_time
      }
      this.dialog = true
    },

    close () {
      this.dialog = false
      this.editedItem = getEmptyDay()
    },
    save () {
      const isStartTime = Boolean(this.editedItem.start_time)
      const isEndTime = Boolean(this.editedItem.end_time)

      const [startHours, startMinutes] = isStartTime ? this.editedItem.start_time.split(':') : []
      const [endHours, endMinutes] = isEndTime ? this.editedItem.end_time.split(':') : []
      const toMin = function (h, m) {
        return Number(h) * 60 + Number(m)
      }

      if (isStartTime && isEndTime) {
        const dayShift = toMin(startHours, startMinutes) < toMin(endHours, endMinutes) ? 0 : 24
        this.editedItem.worked_hours = (endHours - startHours + dayShift) + ((endMinutes - startMinutes - this.schema.break_minutes) / 60)
        this.editedItem.working = 1
      }

      Object.assign(this.day, this.editedItem)
      this.day.changed_ex_post = this.hasEditedWorktime
      this.close()
    }
  },
  watch: {
    day: {
      handler (day) {
        const { start_time, end_time } = day
        this.initialWorkTime.start_time = start_time
        this.initialWorkTime.end_time = end_time
      },
      immediate: true
    }
  },
  i18n: {
    messages: {
      de: {
        holiday: 'Feiertag',
        duration: '{h} Std. {m} Min.',
        isLeaveDay: 'Urlaubstag',
        isHoliday: 'Feiertag',
        unusualWorkTimeWarning: 'Arbeitszeitdifferenz von mehr als 25% zum Durchschnitt',
        changeWorkTime: 'Arbeitszeit anpassen',
        deleteWorkTime: 'Arbeitszeit entfernen',
        noContract: 'Kein Vertrag',
        isInactive: 'Inaktiv'
      },
      en: {
        holiday: 'Holiday',
        duration: '{h} h. {m} m.',
        isLeaveDay: 'Vacation',
        isHoliday: 'National holiday',
        unusualWorkTimeWarning: 'Working time difference of more than 25% compared to average',
        changeWorkTime: 'Change work time',
        deleteWorkTime: 'Remove work time',
        noContract: 'No contract',
        isInactive: 'Inactive'
      },
      tr: {
        holiday: 'Tatil',
        duration: '{h} s. {m} dk.',
        isHoliday: 'Ulusal tatil',
        isLeaveDay: 'Tatil',
        unusualWorkTimeWarning: 'Çalışma süresinin ortalamaya göre% 25 den fazla olması',
        changeWorkTime: 'çalışma zamanını değiştir',
        deleteWorkTime: 'çalışma zamanını sil',
        noContract: 'Hiçbir sözleşme',
        isInactive: 'Etkisiz'
      },
      ru: {
        holiday: 'Выходной',
        duration: '{h} ч. {m} м.',
        isHoliday: 'Национальный праздник',
        isLeaveDay: 'Отпуск',
        unusualWorkTimeWarning: 'Разница в рабочем времени более чем на 25% по сравнению со средним показателем',
        changeWorkTime: 'Изменить время работы',
        deleteWorkTime: 'удалить рабочее время',
        noContract: 'Без контракта',
        isInactive: 'Неактивный'
      }
    }
  }
}
</script>

<style scoped>

  .disabled {
    color: #757575
  }
  .edited {
    background-color: #FFECB3
  }
  .holiday {
    background-color: #F5F5F5
  }
</style>
